import { useEffect, useState } from "react";
import { isoToDmy, dmyToIso } from "@/lib/date";

/**
 * Text input that displays dates in DD-MM-YYYY (Indian convention) while
 * storing values as ISO YYYY-MM-DD upstream.
 *
 * Accepts either format on paste — auto-converts on blur. Empty input clears
 * the value. Matches the Swiss-layout Field component visually.
 *
 * Props:
 *  - label: visible eyebrow label (string)
 *  - value: ISO string stored upstream (YYYY-MM-DD or empty)
 *  - onChange(iso): called with normalised ISO string (or "" when cleared)
 *  - testId: data-testid for the input
 */
export default function DateField({ label, value, onChange, testId }) {
  // Local text state is what the user sees; upstream value is always ISO.
  const [text, setText] = useState(isoToDmy(value));

  // Re-sync if upstream value changes (e.g. form reset, async load).
  useEffect(() => {
    setText(isoToDmy(value));
  }, [value]);

  const commit = (raw) => {
    const trimmed = (raw || "").trim();
    if (!trimmed) {
      setText("");
      onChange("");
      return;
    }
    const iso = dmyToIso(trimmed);
    if (iso) {
      setText(isoToDmy(iso));
      onChange(iso);
    } else {
      // Invalid input — keep what user typed, don't update upstream yet.
      setText(trimmed);
    }
  };

  return (
    <label className="block">
      <span className="label-eyebrow block mb-2">{label}</span>
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        placeholder="DD-MM-YYYY"
        data-testid={testId}
        inputMode="numeric"
        className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:ring-offset-1 focus:border-[#002FA7] bg-white"
      />
    </label>
  );
}
